import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../lib";

export interface SelectOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  title?: string;
  placeholder?: string;
  triggerPrefix?: string;
  className?: string;
  contentClassName?: string;
  showRadioUnselected?: boolean;
}

const Select = ({
  className,
  contentClassName,
  options,
  value,
  onChange,
  title = "Sort by",
  placeholder = "Select option",
  triggerPrefix,
  showRadioUnselected,
}: SelectProps) => {
  const [open, setOpen] = React.useState(false);
  const [draftValue, setDraftValue] = React.useState(value ?? "");

  const selectedOption = options.find((option) => option.value === value);

  React.useEffect(() => {
    if (!open) return;
    setDraftValue(value ?? "");
  }, [open, value]);

  const applySelection = () => {
    if (draftValue && draftValue !== value) {
      onChange?.(draftValue);
    }
    setOpen(false);
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center justify-between gap-2.5 py-1.5 rounded-xl bg-[#F8F9FF] px-4 text-sm font-medium text-gray-30 transition-colors hover:bg-[#EEF1FB] outline-none",
            open && "bg-[#EEF1FB]",
            className,
          )}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="truncate">
            {triggerPrefix ? (
              <>
                <span className="font-normal text-[#3A3E5B]">
                  {triggerPrefix}:{" "}
                </span>
                <span className="font-semibold text-[#2C2F44]">
                  {selectedOption?.label ?? placeholder}
                </span>
              </>
            ) : (
              selectedOption?.label ?? placeholder
            )}
          </span>
          <ChevronDown
            size={16}
            className={cn(
              "text-gray-20 transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          side="bottom"
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={16}
          className={cn(
            "z-[120] w-[260px] max-w-[calc(100vw-2rem)] rounded-[16px] border border-[#E4E7F4] bg-white p-3.5 shadow-[0_16px_36px_rgba(15,20,40,0.12)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            contentClassName,
          )}
        >
          <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-[#667085]">
            {title}
          </p>

          <div
            className="mt-3 max-h-[320px] space-y-0.5 overflow-y-auto pr-1"
            role="listbox"
          >
            {options.map((option) => {
              const isSelected = draftValue === option.value;
              const showUnselectedRadio = showRadioUnselected ?? !option.icon;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDraftValue(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  className="flex w-full items-center justify-between rounded-[8px] px-2.5 py-2 text-left transition-colors hover:bg-[#F8F9FF]"
                >
                  <div className="flex items-center gap-2.5 truncate">
                    {option.icon && (
                      <span className="flex shrink-0 items-center justify-center">
                        {option.icon}
                      </span>
                    )}
                    <span className="truncate text-[13px] font-medium text-[#1D2939]">
                      {option.label}
                    </span>
                  </div>

                  {isSelected ? (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#6F00FF] text-white">
                      <Check size={10} strokeWidth={3.5} />
                    </span>
                  ) : showUnselectedRadio ? (
                    <span className="h-4 w-4 shrink-0 rounded-full border border-[#D0D5DD]" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="mt-4 flex h-[40px] w-full items-center justify-center rounded-[8px] bg-[#0A0D14] text-[14px] font-medium text-white transition-colors hover:bg-[#1f2937]"
            onClick={applySelection}
          >
            Apply
          </button>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export { Select };
